// /finance/tax-refund — 出口退税
// v1.31: 月度聚合 + 退税率选择 (9% / 13% / 16%) + 单订单退税计算
// 公式: taxBase = productCostCents / 1.13 (或 * 0.93 简化), refund = taxBase * rate
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { CardSkeleton } from '../components/Skeleton';
import { customerApi, financeApi, orderApi } from '../lib/api';
import { fmtMoney, fmtMoneyCompact, fmtPctFromValue } from '../lib/format';

export const Route = createFileRoute('/finance/tax-refund')({
  component: TaxRefundList,
});

// v1.31: 导出 for unit test
export { TaxRefundList };

const RATES = [0.09, 0.13, 0.16] as const;
const RATE_LABELS: Record<number, string> = {
  0.09: '9% (纸品 / 部分塑料)',
  0.13: '13% (玩具 / 手办 / 多数塑料)',
  0.16: '16% (特殊)',
};

function TaxRefundList() {
  const [rate, setRate] = useState<number>(0.13);
  const summaryQ = useQuery({
    queryKey: ['finance', 'tax-refund', 'summary', rate],
    queryFn: () => financeApi.taxRefundSummary(rate),
    refetchInterval: 300000,
  });
  const ordersQ = useQuery({
    queryKey: ['orders', 'for-tax'],
    queryFn: () => orderApi.list(),
  });
  const customersQ = useQuery({
    queryKey: ['customers', 'lookup', 'tax'],
    queryFn: () => customerApi.list({ limit: 200 }),
  });

  const customerById = new Map((customersQ.data?.items ?? []).map((c) => [c.id, c]));

  const summaries = summaryQ.data?.items ?? [];
  const orders = (ordersQ.data?.items ?? []).filter(
    (o) => (o as { productCostCents?: number | null }).productCostCents != null,
  );

  const totalRefund = summaries.reduce((acc, s) => acc + s.totalRefundCents, 0);
  const totalOrders = summaries.reduce((acc, s) => acc + s.orderCount, 0);
  const currency = summaries[0]?.currency ?? 'CNY';

  return (
    <div>
      {/* Header */}
      <div className="bg-surface border border-line rounded p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Icon name="receipt" size="sm" className="text-info" />
            退税率
          </h2>
          <p className="text-[10px] text-ink-3 mt-0.5">
            玩具 / 手办 HS 9503/9502 默认 13% · 纸品 9% · 特殊 16%
          </p>
        </div>
        <div className="inline-flex border border-line rounded overflow-hidden">
          {RATES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRate(r)}
              className={`px-3 py-1.5 text-xs border-l border-line first:border-l-0 ${
                rate === r ? 'bg-accent text-white' : 'bg-surface text-ink-2 hover:bg-surface-2'
              }`}
              title={RATE_LABELS[r]}
            >
              {fmtPctFromValue(r * 100, 0)}
            </button>
          ))}
        </div>
      </div>

      {/* Total */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <SummaryCard
          label="累计退税"
          value={fmtMoneyCompact(totalRefund, currency)}
          sub={`${summaries.length} 月 · ${totalOrders} 单`}
          loading={summaryQ.isLoading}
        />
        <SummaryCard
          label="当前退税率"
          value={fmtPctFromValue(rate * 100, 0)}
          sub={RATE_LABELS[rate] ?? ''}
          loading={false}
        />
        <SummaryCard
          label="退税基数公式"
          value="成本 ÷ 1.13"
          sub="税基 = 不含税成本, × 退税率 = 退税金额"
          loading={false}
        />
      </div>

      {/* Monthly */}
      <div className="bg-surface border border-line rounded mb-5">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h2 className="text-sm font-semibold">月度退税</h2>
          <button
            type="button"
            onClick={() => summaryQ.refetch()}
            disabled={summaryQ.isFetching}
            className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-1"
          >
            <Icon
              name="refresh-cw"
              size="xs"
              className={summaryQ.isFetching ? 'animate-spin' : ''}
            />
            {summaryQ.isFetching ? '刷新中' : '刷新'}
          </button>
        </div>
        {summaryQ.isLoading ? (
          <div className="p-4">
            <CardSkeleton />
          </div>
        ) : summaries.length === 0 ? (
          <EmptyState
            icon="receipt"
            title="暂无退税记录"
            description="订单录入 productCostCents 后会自动算退税"
          />
        ) : (
          <div className="divide-y divide-line">
            {summaries.slice(0, 12).map((s) => (
              <div key={s.month} className="px-4 py-3 flex items-center gap-3 hover:bg-surface-2">
                <div className="w-20 text-xs font-mono text-ink-2">{s.month}</div>
                <div className="flex-1 text-xs text-ink-3">
                  {s.orderCount} 单 · 退税率 {fmtPctFromValue(s.refundRate * 100, 0)}
                </div>
                <div className="text-sm font-semibold text-info">
                  {fmtMoney(s.totalRefundCents, s.currency)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Orders with cost — list */}
      <div className="bg-surface border border-line rounded">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="text-sm font-semibold">有产品成本的订单 (Top 20)</h2>
        </div>
        {ordersQ.isLoading ? (
          <div className="p-4">
            <CardSkeleton />
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon="receipt"
            title="没有可退税订单"
            description="订单详情里录入 productCostCents 后会出现在这里"
          />
        ) : (
          <div className="divide-y divide-line">
            {orders.slice(0, 20).map((o) => {
              const productCostCents =
                (o as { productCostCents?: number | null }).productCostCents ?? 0;
              const taxBase = Math.round(productCostCents * 0.93);
              const refund = Math.round(taxBase * rate);
              const customer = customerById.get(o.customerId);
              return (
                <div key={o.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {o.poNumber || o.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-ink-3 truncate">
                      {customer?.name ?? '—'} · 成本 {fmtMoney(productCostCents, o.currency)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-info">
                      {fmtMoney(refund, o.currency)}
                    </div>
                    <div className="text-[10px] text-ink-3">
                      税基 {fmtMoney(taxBase, o.currency)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-[10px] text-ink-3 text-center mt-4 space-y-0.5">
        <p>注: 真实公式 cost / 1.13, 当前用 0.93 简化 (教学值, 跟 v1.18 lib 一致)</p>
        <p>CN 主体出口订单可申报 · JP/HK 主体不退税</p>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  loading,
}: {
  label: string;
  value: string;
  sub: string;
  loading: boolean;
}) {
  return (
    <div className="bg-surface border border-line rounded p-3">
      <div className="text-[10px] text-ink-3 mb-1">{label}</div>
      {loading ? (
        <div className="h-6 w-24 bg-surface-2 rounded animate-pulse" />
      ) : (
        <div className="text-base sm:text-lg font-semibold">{value}</div>
      )}
      <div className="text-[10px] text-ink-3 mt-0.5">{sub}</div>
    </div>
  );
}
